import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import {
  CreditCard,
  KeyRound,
  Link2,
  Loader2,
  User,
  Users,
  Webhook,
  X,
  Lock,
  Sparkles,
  CalendarClock,
  Cable,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Input } from '@/components/ui/input'
import { useAuthSessionStore } from '@/features/auth/stores/auth-session-store'
import { deleteAccountRemote, fetchCurrentSession, fetchMembershipSummary, sendEmailVerificationCode } from '@/features/auth/services/auth-service'
import { useMembershipDialogStore } from '@/features/billing/stores/membership-dialog-store'
import { useCreditRechargeDialogStore } from '@/features/billing/stores/credit-recharge-dialog-store'
import {
  formatBillingFeature,
  formatMembershipExpiresAt,
  formatMembershipPlan,
  formatMembershipStatus,
} from '@/features/billing/utils/membership-labels'
import { appFetch } from '@/shared/api/app-client'
import type { ApiResponse, Studio } from '@/shared/api/app-types'
import { LocalMcpStatusPanel } from '@/features/settings/components/local-mcp-status-panel'
import { useSettingsStore } from '@/features/settings/stores/settings-store'
import { toast } from 'sonner'

/* ------------------------------------------------------------------ */
/* 导航                                                                */
/* ------------------------------------------------------------------ */

type Section = 'account' | 'appearance' | 'billing' | 'studio' | 'socials' | 'api-keys' | 'webhooks' | 'mcp'

const settingsNav: { key: Section; label: string; icon: typeof User }[] = [
  { key: 'account', label: '账户', icon: User },
  { key: 'appearance', label: '外观与动效', icon: Sparkles },
  { key: 'billing', label: '账单与用量', icon: CreditCard },
  { key: 'studio', label: '当前工作区', icon: Users },
  { key: 'socials', label: '连接社交账号', icon: Link2 },
  { key: 'api-keys', label: 'API 密钥', icon: KeyRound },
  { key: 'webhooks', label: 'Webhooks', icon: Webhook },
  { key: 'mcp', label: 'MCP', icon: Cable },
]

/* ------------------------------------------------------------------ */
/* 通用小组件                                                          */
/* ------------------------------------------------------------------ */

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="py-4 sm:flex">
      <dt className="font-semibold text-foreground sm:w-[167px] sm:flex-none sm:pr-8 sm:pl-4">
        {label}
      </dt>
      <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
        <div className="text-muted-foreground">{value}</div>
      </dd>
    </div>
  )
}

function PanelHeading({ title, description }: { title: string; description?: string }) {
  return (
    <div>
      <h2 className="hidden text-sm font-semibold text-foreground sm:block">{title}</h2>
      {description ? (
        <p className="mt-1.5 text-xs font-medium text-muted-foreground">{description}</p>
      ) : null}
    </div>
  )
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-[200px] flex-col items-center justify-center gap-3 py-12 text-center text-sm text-muted-foreground">
      {children}
    </div>
  )
}

function getInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  return (parts[0]?.[0] ?? '用') + (parts[1]?.[0] ?? '')
}

const quotaLabels: Record<string, string> = {
  transcription_seconds: '转录与字幕',
  tts_characters: '云端配音',
  music_seconds: '音乐生成',
  image_generations: 'AI 生图',
  generation_credits: '高级生成点数',
  ai_processing_seconds: '智能切片',
}

function formatFileSize(bytes: number) {
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`
}

function formatQuotaUnits(metric: string, value: number) {
  if (metric === 'transcription_seconds' || metric === 'music_seconds' || metric === 'ai_processing_seconds') {
    return value >= 60 ? `${Math.floor(value / 60)} 分 ${value % 60} 秒` : `${value} 秒`
  }
  if (metric === 'tts_characters') return `${value.toLocaleString()} 字`
  if (metric === 'image_generations') return `${value} 张`
  if (metric === 'generation_credits') return `${value} 点`
  return value.toLocaleString()
}

type UsageEvent = {
  usageNo: string
  capabilityKey: string
  meterType: string
  chargedUnits: number
  status: string
  projectId: number | null
  occurredAt: string
}

/* ------------------------------------------------------------------ */
/* 1. 账户面板                                                          */
/* ------------------------------------------------------------------ */

function AccountPanel() {
  const session = useAuthSessionStore((state) => state.session)
  const clearSession = useAuthSessionStore((state) => state.clearSession)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deletePassword, setDeletePassword] = useState('')
  const [deleteConfirmation, setDeleteConfirmation] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)

  if (!session) {
    return (
      <Centered>
        <Loader2 className="size-6 animate-spin text-muted-foreground/60" />
        正在加载账户信息…
      </Centered>
    )
  }

  const { user } = session

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'DELETE' || !deletePassword) return

    setIsDeleting(true)
    try {
      await deleteAccountRemote({
        password: deletePassword,
        confirmation: 'DELETE',
      })
      clearSession()
      toast.success('账户已注销，正在返回登录页。')
      window.setTimeout(() => window.location.assign('/login'), 500)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '账户注销失败，请稍后重试。')
      setIsDeleting(false)
    }
  }

  return (
    <div>
      <PanelHeading title="账户" description="查看当前登录账户的基础信息。账户资料编辑即将支持。" />

      <div className="mt-4 flex items-center gap-4">
        {user.avatarUrl ? (
          <img src={user.avatarUrl} alt={user.name} className="size-14 rounded-full object-cover" />
        ) : (
          <span className="flex size-14 items-center justify-center rounded-full bg-primary/10 text-lg font-semibold text-primary">
            {getInitials(user.name)}
          </span>
        )}
        <div>
          <div className="text-sm font-semibold text-foreground">{user.name}</div>
          <div className="text-xs text-muted-foreground">{user.email}</div>
        </div>
      </div>

      <div className="mt-4 text-sm font-semibold text-foreground">个人详情</div>
      <dl className="mt-4 divide-y divide-border border-y border-border text-xs">
        <Field label="姓名" value={user.name} />
        <Field label="邮箱地址" value={user.email} />
        <Field label="用户 ID" value={user.id} />
      </dl>
      {!session.emailVerified ? <div className="mt-4 flex items-center justify-between rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs"><span>邮箱尚未验证；验证后可领取免费体验额度。</span><Button size="sm" onClick={() => void sendEmailVerificationCode().then(() => { toast.success('验证码已发送'); window.location.assign('/verify-email?redirect=%2Fapp') }).catch((error) => toast.error(error instanceof Error ? error.message : '发送失败'))}>立即验证</Button></div> : session.membership?.trialGrantStatus === 'held' ? <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">邮箱已验证，免费体验资格正在安全复核中；复核通过后将自动显示额度。</div> : <div className="mt-4 text-xs text-emerald-500">邮箱已验证</div>}

      <h2 className="mt-6 text-sm font-semibold text-foreground">删除我的账户</h2>
      <div className="flex items-center justify-between gap-4">
        <p className="mt-1.5 text-xs font-medium text-muted-foreground">
          注销后将移除账户资料与登录凭据，并退出所有设备。此操作不可撤销。
        </p>
        <Button
          variant="ghost"
          size="sm"
          type="button"
          onClick={() => setDeleteDialogOpen(true)}
          className="text-xs font-semibold text-destructive hover:text-destructive/80"
        >
          删除
        </Button>
      </div>
      <p className="mt-1 text-[11px] text-muted-foreground/70">
        为保护订阅、订单及额度审计记录，相关记录会在注销后去除个人身份信息并按保留规则存档。
      </p>

      <AlertDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          setDeleteDialogOpen(open)
          if (!open && !isDeleting) {
            setDeletePassword('')
            setDeleteConfirmation('')
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认注销账户？</AlertDialogTitle>
            <AlertDialogDescription>
              注销后无法恢复。请输入当前密码，并输入 <strong>DELETE</strong> 确认。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            <Input
              type="password"
              autoComplete="current-password"
              placeholder="当前密码"
              value={deletePassword}
              disabled={isDeleting}
              onChange={(event) => setDeletePassword(event.target.value)}
            />
            <Input
              autoCapitalize="none"
              autoCorrect="off"
              placeholder="输入 DELETE 确认"
              value={deleteConfirmation}
              disabled={isDeleting}
              onChange={(event) => setDeleteConfirmation(event.target.value)}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>取消</AlertDialogCancel>
            <Button
              type="button"
              variant="destructive"
              disabled={isDeleting || !deletePassword || deleteConfirmation !== 'DELETE'}
              onClick={() => void handleDeleteAccount()}
            >
              {isDeleting ? '正在注销…' : '永久注销'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* 2. 外观与动效面板                                                   */
/* ------------------------------------------------------------------ */

function AppearancePanel() {
  const showInterfaceAnimations = useSettingsStore((state) => state.showInterfaceAnimations)
  const setSetting = useSettingsStore((state) => state.setSetting)

  return (
    <div>
      <PanelHeading title="外观与动效" description="控制工作台中的装饰性动态效果，不影响上传、渲染和编辑任务。" />
      <div className="mt-5 rounded-xl border border-border bg-background/50 px-4 py-4">
        <div className="flex items-center justify-between gap-5">
          <div>
            <div className="text-sm font-semibold text-foreground">显示界面动效</div>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">显示顶部粒子流光、升级套餐弹窗的光晕与掠光效果。</p>
          </div>
          <Switch
            checked={showInterfaceAnimations}
            onCheckedChange={(checked) => setSetting('showInterfaceAnimations', checked)}
            aria-label="显示界面动效"
          />
        </div>
      </div>
      <p className="mt-3 text-xs leading-5 text-muted-foreground">此设置保存在当前设备。系统开启“减少动态效果”时，动效也会自动停止。</p>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* 3. 账单与用量面板                                                    */
/* ------------------------------------------------------------------ */

function BillingPanel() {
  const session = useAuthSessionStore((state) => state.session)
  const updateMembership = useAuthSessionStore((state) => state.updateMembership)
  const setSession = useAuthSessionStore((state) => state.setSession)
  const openMembershipDialog = useMembershipDialogStore((state) => state.openDialog)
  const openCreditRechargeDialog = useCreditRechargeDialogStore((state) => state.openDialog)
  const [usageEvents, setUsageEvents] = useState<UsageEvent[]>([])
  const [usageLoading, setUsageLoading] = useState(true)
  const [storageSummary, setStorageSummary] = useState<{ assetUsedBytes: number; assetStorageBytes: number; assetRemainingBytes: number; maxUploadBytes: number; projectRetentionDays: number } | null>(null)

  // 进入时拉取一次最新额度
  useEffect(() => {
    void fetchCurrentSession().then((freshSession) => { if (freshSession) setSession(freshSession) })
    void fetchMembershipSummary()
      .then((membership) => {
        if (membership) updateMembership(membership)
      })
      .catch(() => {
        // 拉取失败时保留登录态里的额度
      })
    void appFetch<{ ok: boolean; data: UsageEvent[] }>('/api/subsite/billing/usage?limit=8')
      .then((response) => setUsageEvents(response.data ?? []))
      .catch(() => setUsageEvents([]))
      .finally(() => setUsageLoading(false))
    void appFetch<ApiResponse<{ assetUsedBytes: number; assetStorageBytes: number; assetRemainingBytes: number; maxUploadBytes: number; projectRetentionDays: number }>>('/api/storage/summary')
      .then((response) => setStorageSummary(response.data))
      .catch(() => setStorageSummary(null))
  }, [setSession, updateMembership])

  const membership = session?.membership
  const isFreeTrial = membership?.planCode === 'fastcut_free'
  const planLabel = formatMembershipPlan(membership?.planName, membership?.planCode)
  const statusLabel = formatMembershipStatus(membership?.status)
  const expiresLabel = formatMembershipExpiresAt(membership?.expiresAt)

  const modules = membership?.modules ?? []
  const featureFlags = membership?.appFeatureFlags ?? []
  const quotas = Object.values(membership?.quotas?.details ?? {})
    .filter((quota): quota is NonNullable<typeof quota> => Boolean(quota))
  const isFreeTrialExhausted = isFreeTrial
    && ['ai_processing_seconds', 'transcription_seconds'].every((metric) =>
      (membership?.quotas?.details?.[metric as keyof typeof membership.quotas.details]?.available ?? 0) <= 0,
    )

  return (
    <div>
      <PanelHeading
        title="账单与用量"
        description="查看当前套餐、订阅状态与 AI 处理额度使用情况。"
      />

      {/* 套餐概览 */}
      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <div className="rounded-2xl border border-border/80 bg-background/60 px-4 py-4">
          <div className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground/80">
            当前套餐
          </div>
          <div className="mt-2 text-lg font-semibold text-foreground">{planLabel}</div>
        </div>
        <div className="rounded-2xl border border-border/80 bg-background/60 px-4 py-4">
          <div className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground/80">
            订阅状态
          </div>
          <div className="mt-2 text-lg font-semibold text-foreground">{statusLabel}</div>
        </div>
        <div className="rounded-2xl border border-border/80 bg-background/60 px-4 py-4">
          <div className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground/80">
            到期时间
          </div>
          <div className="mt-2 flex items-center gap-1.5 text-lg font-semibold text-foreground">
            <CalendarClock className="size-4 text-muted-foreground" />
            {expiresLabel}
          </div>
        </div>
      </div>

      {storageSummary ? <div className="mt-6 rounded-2xl border border-border bg-background/60 p-5">
        <div className="text-sm font-semibold text-foreground">云端存储与作品保存</div>
        <div className="mt-1 text-xs text-muted-foreground">本地项目由你的设备保存，永久保留且不占云端空间。</div>
        <div className="mt-4 grid gap-2 sm:grid-cols-3">
          <div className="rounded-xl bg-card/70 px-3 py-3 text-xs"><span className="block text-muted-foreground">云端素材空间</span><strong className="mt-1 block text-foreground">{formatFileSize(storageSummary.assetUsedBytes)} / {formatFileSize(storageSummary.assetStorageBytes)}</strong></div>
          <div className="rounded-xl bg-card/70 px-3 py-3 text-xs"><span className="block text-muted-foreground">单文件上传</span><strong className="mt-1 block text-foreground">最大 {formatFileSize(storageSummary.maxUploadBytes)}</strong></div>
          <div className="rounded-xl bg-card/70 px-3 py-3 text-xs"><span className="block text-muted-foreground">云端作品自动清理</span><strong className="mt-1 block text-foreground">保存 {storageSummary.projectRetentionDays} 天</strong></div>
        </div>
      </div> : null}

      {/* AI 额度 */}
      <div className="mt-6 rounded-2xl border border-border bg-background/60 p-5">
        <div className="flex items-center gap-3">
          <div className="flex size-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Sparkles className="size-4" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-foreground">{isFreeTrial ? '免费体验额度' : 'AI 额度'}</div>
            <div className="text-xs text-muted-foreground">
              {isFreeTrial
                ? '一次性体验额度，不自动重置；各项能力独立计量。'
                : '各项能力独立计量；高级生成使用单独点数，不占用基础套餐额度。'}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button variant="outline" size="sm" onClick={openCreditRechargeDialog}>
              充值点数
            </Button>
            <Button variant="outline" size="sm" onClick={() => openMembershipDialog()}>
              查看套餐
            </Button>
          </div>
        </div>

        {quotas.length > 0 ? (
          <>
            {isFreeTrialExhausted ? (
              <div className="mt-4 rounded-xl border border-primary/20 bg-primary/5 px-3 py-2 text-xs text-foreground">
                免费体验额度已用完，不会自动重置。请升级套餐继续处理更多视频。
              </div>
            ) : null}
            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              {quotas.map((quota) => {
              const usedPercent = quota.total > 0 ? Math.min(Math.round((quota.used / quota.total) * 100), 100) : 0
              return (
                <div key={quota.metric} className="rounded-xl bg-card/70 px-3 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-xs font-medium text-foreground">{quotaLabels[quota.metric] ?? quota.metric}</span>
                    <span className="text-xs font-semibold text-primary">剩余 {formatQuotaUnits(quota.metric, quota.available)}</span>
                  </div>
                  <div className="mt-1 text-[11px] text-muted-foreground">
                    已用 {formatQuotaUnits(quota.metric, quota.used)}{quota.reserved > 0 ? `，预占 ${formatQuotaUnits(quota.metric, quota.reserved)}` : ''}
                    {quota.resetStrategy === 'never'
                      ? ' · 不自动重置'
                      : quota.resetAt
                        ? ` · ${new Date(quota.resetAt).toLocaleDateString('zh-CN')} 重置`
                        : ''}
                  </div>
                  {quota.total > 0 && quota.metric !== 'generation_credits' ? (
                    <div className="mt-2 h-1 overflow-hidden rounded-full bg-muted">
                      <div className="h-full rounded-full bg-primary" style={{ width: `${usedPercent}%` }} />
                    </div>
                  ) : null}
                </div>
              )
              })}
            </div>
          </>
        ) : (
          <div className="mt-4 rounded-xl border border-border/70 bg-background/40 px-4 py-5 text-center text-xs text-muted-foreground">
            暂未获取到额度配置，开通会员或购买点数后会在这里显示。
          </div>
        )}
      </div>

      {/* 功能权限 */}
      {(modules.length > 0 || featureFlags.length > 0) && (
        <div className="mt-6">
          <div className="text-sm font-semibold text-foreground">已开通功能</div>
          <div className="mt-3 flex flex-wrap gap-2">
            {modules.map((m) => (
              <span
                key={m}
                className="rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground"
              >
                {formatBillingFeature(m) || m}
              </span>
            ))}
            {featureFlags.map((f) => (
              <span
                key={f}
                className="rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs text-primary"
              >
                {formatBillingFeature(f) || f}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* 最近用量 */}
      <div className="mt-6">
        <div className="text-sm font-semibold text-foreground">最近 AI 用量</div>
        {usageLoading ? (
          <div className="mt-3 flex items-center gap-2 rounded-xl border border-border/70 bg-background/40 px-4 py-5 text-xs text-muted-foreground">
            <Loader2 className="size-3.5 animate-spin" /> 正在加载用量记录…
          </div>
        ) : usageEvents.length ? (
          <div className="mt-3 divide-y divide-border overflow-hidden rounded-xl border border-border/70 bg-background/40">
            {usageEvents.map((event) => (
              <div key={event.usageNo} className="flex items-center justify-between gap-4 px-4 py-3 text-xs">
                <div>
                  <div className="font-medium text-foreground">{quotaLabels[event.capabilityKey] ?? event.capabilityKey}</div>
                  <div className="mt-0.5 text-muted-foreground">
                    {new Date(event.occurredAt).toLocaleString('zh-CN')}{event.projectId ? ` · 项目 ${event.projectId}` : ''}
                  </div>
                </div>
                <div className="text-right">
                  <div className={event.status === 'succeeded' ? 'font-medium text-foreground' : 'font-medium text-destructive'}>
                    {event.status === 'succeeded' ? `消耗 ${formatQuotaUnits(event.capabilityKey === 'tts' ? 'tts_characters' : event.meterType === 'images' ? 'image_generations' : event.meterType === 'credits' ? 'generation_credits' : event.meterType === 'seconds' ? 'transcription_seconds' : event.meterType, event.chargedUnits)}` : '未扣费'}
                  </div>
                  <div className="mt-0.5 text-muted-foreground">{event.status === 'succeeded' ? '已完成' : event.status === 'failed' ? '失败' : event.status}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-3 rounded-xl border border-border/70 bg-background/40 px-4 py-6 text-center text-xs text-muted-foreground">
            暂无 AI 用量记录
          </div>
        )}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* 3. 管理 Studio 面板                                                  */
/* ------------------------------------------------------------------ */

function StudioPanel() {
  const [studio, setStudio] = useState<Studio | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError(null)
      try {
        const res = await appFetch<ApiResponse<Studio>>('/api/studios/current')
        if (!cancelled) setStudio(res.data)
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : '加载 Studio 信息失败')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  if (loading) {
    return (
      <Centered>
        <Loader2 className="size-6 animate-spin text-muted-foreground/60" />
        正在加载 Studio 信息…
      </Centered>
    )
  }
  if (error || !studio) {
    return (
      <Centered>
        <p className="text-destructive">{error ?? '未找到 Studio 信息'}</p>
      </Centered>
    )
  }

  return (
    <div>
      <PanelHeading
        title="当前工作区"
        description="工作区用于归属你的项目、素材和品牌模板；它不是订阅信息，套餐与额度请在“账单与用量”查看。"
      />
      <dl className="mt-4 divide-y divide-border border-y border-border text-xs">
        <Field label="工作区名称" value={studio.displayName} />
        <Field label="你的角色" value={studio.role === 'owner' ? '所有者' : studio.role} />
        <Field label="状态" value={studio.status === 'active' ? '正常使用中' : studio.status} />
      </dl>
      <p className="mt-4 text-xs leading-5 text-muted-foreground">
        工作区改名、成员邀请与多工作区切换即将开放。
      </p>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* 4. API 密钥面板                                                      */
/* ------------------------------------------------------------------ */

function ApiKeysPanel() {
  return (
    <div>
      <PanelHeading
        title="开发者 API"
        description="用于将 JIAN 能力接入你的产品与自动化流程。"
      />
      <Centered>
        <KeyRound className="size-8 text-muted-foreground/40" />
        开发者 API 即将开放
        <p className="max-w-sm text-xs leading-5 text-muted-foreground/70">
          公开 API、密钥创建与调用用量统计将在后续版本逐步推出。
        </p>
      </Centered>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* 5. 连接社交账号面板（一期发布入口）                                    */
/* ------------------------------------------------------------------ */

const socialPlatforms = [
  { name: '抖音', detail: '授权接入准备中，连接后可直接发布和定时发布', direct: true },
  { name: '快手', detail: '授权接入准备中，连接后可直接发布和定时发布', direct: true },
  { name: '小红书', detail: '一期支持复制文案与链接，在 App 内完成发布', direct: false },
  { name: '微博', detail: '一期支持复制文案与链接，直发权限待平台确认', direct: false },
  { name: '知乎', detail: '一期支持复制文案与链接，适合图文/链接分享', direct: false },
]

function SocialsPanel() {
  return (
    <div>
      <PanelHeading
        title="连接社交账号"
        description="社交账号授权、直接发布与定时发布正在准备中。"
      />
      <div className="mt-4 rounded-xl border border-dashed border-border bg-background/40 px-4 py-5 text-center text-xs text-muted-foreground">
        当前可通过分享链接和复制文案完成平台交接；账号授权即将开放。
      </div>
      <div className="mt-4 space-y-3">
        {socialPlatforms.map((p) => (
          <div
            key={p.name}
            className="flex items-center justify-between rounded-xl border border-border bg-card px-4 py-3 opacity-60"
          >
            <div>
              <div className="text-sm font-medium text-foreground">{p.name}</div>
              <div className="mt-0.5 text-xs text-muted-foreground">{p.detail}</div>
            </div>
            <Button variant="outline" size="sm" disabled className="gap-1.5">
              <Lock className="size-3" />
              即将开放
            </Button>
          </div>
        ))}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* 6. Webhooks 面板（mock 占位）                                        */
/* ------------------------------------------------------------------ */

function WebhooksPanel() {
  return (
    <div>
      <PanelHeading title="Webhooks" description="事件回调能力正在准备中。" />
      <div className="mt-4 rounded-xl border border-dashed border-border bg-background/40 px-4 py-5 text-center text-xs text-muted-foreground">
        Webhooks 即将开放，届时可订阅视频生成、导出完成等事件。
      </div>
      <Centered>
        <Webhook className="size-8 text-muted-foreground/40" />
        暂无 Webhook
      </Centered>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* 7. MCP 面板                                                         */
/* ------------------------------------------------------------------ */

function McpPanel() {
  return (
    <div>
      <PanelHeading
        title="MCP"
        description="连接 Codex、Cursor 等 MCP 客户端，让 AI 操作当前 JIAN 项目。"
      />
      <div className="mt-5">
        <LocalMcpStatusPanel />
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* 主容器                                                               */
/* ------------------------------------------------------------------ */

export function SettingsCanvas({ initialSection = 'account' }: { initialSection?: Section }) {
  const [activeSection, setActiveSection] = useState<Section>(initialSection)

  useEffect(() => {
    setActiveSection(initialSection)
  }, [initialSection])

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-background/80 px-5 py-6 backdrop-blur-sm">
      <div className="mt-4 w-full max-w-[960px] rounded-xl border border-border bg-background p-1.5 shadow-2xl">
        {/* header */}
        <div className="flex flex-row items-center justify-between px-2 text-left">
          <h2 className="text-base font-semibold text-foreground">设置</h2>
          <Link
            to="/app"
            className="inline-flex size-8 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <X className="size-4" />
          </Link>
        </div>

        {/* content */}
        <div className="flex">
          {/* left nav */}
          <div className="hidden w-[210px] shrink-0 px-2 py-3 sm:block">
            <ul className="space-y-1">
              {settingsNav.map((item) => {
                const Icon = item.icon
                const active = item.key === activeSection
                return (
                  <li key={item.key}>
                    <button
                      type="button"
                      onClick={() => setActiveSection(item.key)}
                      className={`flex w-full cursor-pointer items-center gap-x-3 rounded-md px-2 py-1.5 text-sm font-medium transition-colors ${
                        active
                          ? 'bg-accent text-foreground'
                          : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                      }`}
                    >
                      <Icon className="size-4" />
                      {item.label}
                    </button>
                  </li>
                )
              })}
            </ul>
          </div>

          {/* right content panel */}
          <div className="h-[592px] grow overflow-y-auto rounded-xl border border-border bg-card p-4 sm:h-[min(760px,80svh)]">
            {activeSection === 'account' ? <AccountPanel /> : null}
            {activeSection === 'appearance' ? <AppearancePanel /> : null}
            {activeSection === 'billing' ? <BillingPanel /> : null}
            {activeSection === 'studio' ? <StudioPanel /> : null}
            {activeSection === 'socials' ? <SocialsPanel /> : null}
            {activeSection === 'api-keys' ? <ApiKeysPanel /> : null}
            {activeSection === 'webhooks' ? <WebhooksPanel /> : null}
            {activeSection === 'mcp' ? <McpPanel /> : null}
          </div>
        </div>
      </div>
    </div>
  )
}
